import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAtlas } from "./athleteServices";
import { fitStrength, timeAgo } from "../../ForBrands/brandDealUtils";
import BrandLogo from "../../common/BrandLogo";
import { Badge, Btn, Card, EmptyState, ErrorState, InsightBox, Loading, Overline, PageHeader } from "../../ForBrands/ui/BrandUI";
import { MobileCard, MobileEmpty, MobilePageHead } from "../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";

// Athlete Mode · Atlas: the brands whose audience matches the athlete's
// profile (the weekly LLM brand-fit matches), with the activation idea for
// each. Brands reach the athlete through offers; Atlas shows who fits.
//
// Below 640px (the phone theme) the same content renders with the phone
// tokens (Figma 2034:1469 is only a stand-in, D-a24): the phone head without
// the eyebrow, mobile cards, the compact insight. "View my profile" is left
// out there, the Profile tab sits in the bottom nav.

const EMPTY_TEXT =
  "No matches yet. Complete your profile, add your interests and link your social accounts so Atlas can find brands that fit you.";

const HOW_ATLAS_WORKS =
  "Atlas compares your sport, position, audience size, engagement and interests with the brands in the Cache pool, then keeps the strongest fits. Brands see the same fit score on their side of Discover, so a strong Atlas match is a brand likely to send you an offer.";

const fitLabel = (m, fit) => `${fit.label} fit${m.score != null ? ` · ${Math.round(m.score)}` : ""}`;

const incumbencyText = (m) =>
  m.incumbencyBasis === "personal_deal" ? "You already work with this brand." : "You listed this brand as an affiliation.";

// A match on a phone: the 48px logo box, brand and category, the fit badge,
// then the activation idea, flags and affiliation note as on desktop.
const PhoneMatchCard = ({ m }) => {
  const fit = fitStrength(m.score);
  return (
    <MobileCard as="li">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] border border-bm-border bg-white">
          <BrandLogo domain={m.domain} mono={(m.brand || "?").slice(0, 2).toUpperCase()} size={36} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
          <h2 className="bm-h-s text-bm-ink">{m.brand}</h2>
          <p className="bm-body-s text-bm-muted">{m.category || m.domain}</p>
        </div>
        <Badge tone="outline" dot={fit.dot} className="shrink-0">
          {fitLabel(m, fit)}
        </Badge>
      </div>
      {m.activation && (
        <div className="flex flex-col gap-1 rounded-[12px] bg-bm-subtle px-4 py-3">
          <Overline>Activation idea</Overline>
          <p className="bm-body-m text-bm-ink">{m.activation}</p>
        </div>
      )}
      {(m.flags || []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {m.flags.map((f) => (
            <Badge key={f} tone="passive">
              {f}
            </Badge>
          ))}
        </div>
      )}
      {m.incumbencyBasis && m.incumbencyBasis !== "none" && <p className="bm-body-s text-bm-muted">{incumbencyText(m)}</p>}
    </MobileCard>
  );
};

const AthleteAtlasPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const phone = usePhoneTheme();

  useEffect(() => {
    fetchAtlas()
      .then((payload) => {
        setData(payload);
        setError(null);
      })
      .catch((err) => setError(err.message || "Could not load Atlas"));
  }, []);

  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const matches = data.matches || [];
  const strong = matches.filter((m) => (m.score || 0) >= 70).length;
  const subtitle = matches.length
    ? `${matches.length} brand${matches.length === 1 ? "" : "s"} match your audience and story${strong ? `, ${strong} strong fit${strong === 1 ? "" : "s"}` : ""}. Matches refresh weekly.`
    : "Brands whose audience matches yours. Matches refresh weekly once your profile and socials are linked.";
  const refreshed = data.computedAt ? ` Last refreshed ${timeAgo(data.computedAt)}.` : "";

  if (phone) {
    return (
      <div className="flex flex-col gap-[18px]">
        <MobilePageHead title="Atlas" sub={subtitle} />
        {matches.length === 0 ? (
          <MobileEmpty
            action={
              <Btn size="lg" className="w-full" onClick={() => navigate("/athlete/profile/edit")}>
                Complete profile
              </Btn>
            }
          >
            {EMPTY_TEXT}
          </MobileEmpty>
        ) : (
          <ul className="flex flex-col gap-[18px]">
            {matches.map((m) => (
              <PhoneMatchCard key={`${m.brand}-${m.domain}`} m={m} />
            ))}
          </ul>
        )}
        <InsightBox compact title="How Atlas works">
          {HOW_ATLAS_WORKS}
          {refreshed}
        </InsightBox>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Athlete Mode"
        title="Atlas"
        subtitle={subtitle}
        actions={<Btn variant="secondary" onClick={() => navigate("/athlete/profile")}>View my profile</Btn>}
      />

      <div className="flex flex-col gap-5 pt-2">
        {matches.length === 0 ? (
          <EmptyState action={<Btn onClick={() => navigate("/athlete/profile/edit")}>Complete profile</Btn>}>{EMPTY_TEXT}</EmptyState>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {matches.map((m) => {
              const fit = fitStrength(m.score);
              return (
                <Card key={`${m.brand}-${m.domain}`} padding="p-5" className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-3">
                    <span className="flex items-center gap-3">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] border border-bm-border bg-white"><BrandLogo domain={m.domain} mono={(m.brand || "?").slice(0, 2).toUpperCase()} size={36} /></span>
                      <span className="flex flex-col">
                        <span className="bm-h-s text-bm-ink">{m.brand}</span>
                        <span className="bm-body-s text-bm-muted">{m.category || m.domain}</span>
                      </span>
                    </span>
                    <Badge tone="outline" dot={fit.dot}>
                      {fitLabel(m, fit)}
                    </Badge>
                  </div>
                  {m.activation && (
                    <div className="rounded-[8px] bg-bm-subtle px-4 py-3">
                      <Overline>Activation idea</Overline>
                      <p className="bm-body-m text-bm-ink">{m.activation}</p>
                    </div>
                  )}
                  {(m.flags || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {m.flags.map((f) => (
                        <Badge key={f} tone="passive">
                          {f}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {m.incumbencyBasis && m.incumbencyBasis !== "none" && (
                    <span className="bm-body-s text-bm-muted">{incumbencyText(m)}</span>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        <InsightBox title="How Atlas works">
          {HOW_ATLAS_WORKS}
          {refreshed}
        </InsightBox>
      </div>
    </div>
  );
};

export default AthleteAtlasPage;
